// CU-86d45b2z1 — browse-only tile: no cart action until the Shopping Cart task ships a complete path.
import React, { useState } from "react";
import { Card, Radio, Tag, Typography } from "antd";
import { PictureOutlined } from "@ant-design/icons";

import type { CustomerCatalogProduct } from "../../api/customer/customerCatalogApi";
import { productImageUrl } from "../../utils/inventory/productImageUrl";
import { formatPrice, initialVariant, isVariantAvailable, primaryImage, variantLabel } from "./catalogPresentation";

const { Text, Paragraph } = Typography;

interface CustomerProductCardProps {
	product: CustomerCatalogProduct;
}

const CustomerProductCard: React.FC<CustomerProductCardProps> = ({ product }) => {
	const [selectedVariantId, setSelectedVariantId] = useState<number | undefined>(initialVariant(product)?.variant_id);
	const variant = product.variants.find((candidate) => candidate.variant_id === selectedVariantId) ?? product.variants[0];
	const image = primaryImage(product.images);
	const available = variant ? isVariantAvailable(variant) : false;

	return (
		<Card
			hoverable={false}
			data-testid={`catalog-product-${product.product_id}`}
			style={{ height: "100%", display: "flex", flexDirection: "column" }}
			styles={{ body: { display: "flex", flexDirection: "column", gap: 8, flex: 1 } }}
			cover={
				image ? (
					<img
						src={productImageUrl(image.file_id)}
						alt={image.alt_text || product.name}
						style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover" }}
					/>
				) : (
					<div
						aria-hidden="true"
						style={{
							width: "100%",
							aspectRatio: "1 / 1",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "var(--mg-bg-subtle)",
							color: "var(--mg-text-secondary)",
							fontSize: 40,
						}}
					>
						<PictureOutlined />
					</div>
				)
			}
		>
			{product.category && (
				<Text type="secondary" style={{ fontSize: 12 }}>
					{product.category.name}
				</Text>
			)}

			<Text strong style={{ fontSize: 15 }}>
				{product.name}
			</Text>

			{product.description && (
				<Paragraph type="secondary" style={{ marginBottom: 0 }} ellipsis={{ rows: 3, expandable: true, symbol: "more" }}>
					{product.description}
				</Paragraph>
			)}

			{product.variants.length > 1 && (
				<Radio.Group
					size="small"
					optionType="button"
					buttonStyle="outline"
					value={variant?.variant_id}
					onChange={(event) => setSelectedVariantId(event.target.value)}
					aria-label={`Options for ${product.name}`}
					options={product.variants.map((candidate) => ({ value: candidate.variant_id, label: variantLabel(candidate) }))}
				/>
			)}

			<div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
				<Text strong style={{ fontSize: 16 }} data-testid={`catalog-price-${product.product_id}`}>
					{formatPrice(variant?.price)}
				</Text>
				<Tag color={available ? "success" : "default"} data-testid={`catalog-availability-${product.product_id}`}>
					{available ? "In stock" : "Out of stock"}
				</Tag>
			</div>
		</Card>
	);
};

export default CustomerProductCard;
